import { useState, useEffect } from "react"
import { LoadBalancerService, OrganizationWithLoadBalancer, VendorIntake } from "@/features/fmo"
import {
  LoadBalancerConfig,
  LoadBalancerURL,
  Organization,
  LoadBalancerFilters as LoadBalancerFiltersType,
  PaginationState,
  NewConfigState
} from "@/features/fmo"
import { validatePercentages, autoConfigurePercentages } from "./loadBalancer/utils/validation"

// Components
import LoadBalancerHeader from "./loadBalancer/components/LoadBalancerHeader"
import LoadBalancerFilters from "./loadBalancer/components/LoadBalancerFilters"
import ErrorDisplay from "./loadBalancer/components/ErrorDisplay"
import LoadBalancerConfigCard from "./loadBalancer/components/LoadBalancerConfigCard"
import LoadBalancerPagination from "./loadBalancer/components/LoadBalancerPagination"
import ConfigurationModal from "./loadBalancer/components/ConfigurationModal"
import CreateModal from "./loadBalancer/components/CreateModal"
import LoadBalancerLoading from "./loadBalancer/components/LoadBalancerLoading"
import LoadBalancerEmptyState from "./loadBalancer/components/LoadBalancerEmptyState"

export default function FMOLoadBalancer() {
  const [loadBalancers, setLoadBalancers] = useState<LoadBalancerConfig[]>([])
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [vendorIntakes, setVendorIntakes] = useState<VendorIntake[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isAutoConfiguring, setIsAutoConfiguring] = useState(false)

  // Modal states
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedConfig, setSelectedConfig] = useState<LoadBalancerConfig | null>(null)
  const [tempUrls, setTempUrls] = useState<LoadBalancerURL[]>([])

  // Filters and pagination
  const [filters, setFilters] = useState<LoadBalancerFiltersType>({
    searchTerm: "",
    statusFilter: "all",
    typeFilter: "all"
  })
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    itemsPerPage: 10,
    totalPages: 0
  })

  // New configuration state
  const [newConfig, setNewConfig] = useState<NewConfigState>({
    organizationId: "",
    assignedLinks: [],
  })

  useEffect(() => {
    loadData()
  }, [])

  // Reset to first page when filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, currentPage: 1 }))
  }, [filters.searchTerm, filters.statusFilter, filters.typeFilter, pagination.itemsPerPage])

  const loadData = async () => {
    try {
      setLoading(true)

      // Fetch real data from backend
      const [loadBalancerData, organizationsData, vendorIntakesData] = await Promise.all([
        LoadBalancerService.getConfigurations(),
        LoadBalancerService.getOrganizations(),
        LoadBalancerService.getVendorIntakes()
      ])

      // Transform backend data to match component structure
      const transformedLoadBalancers: LoadBalancerConfig[] = loadBalancerData.map((org: OrganizationWithLoadBalancer) => {
        
        const assignedLinks = org.assignedLinks.map((link, index) => ({
          id: `${org.id}-${index}`,
          linkId: link.linkId,
          percentage: link.percentage,
              isActive: true,
          vendorIntake: link.vendorIntake
        }))

        const totalPercentage = assignedLinks.reduce((sum, link) => sum + link.percentage, 0)

        return {
          id: org.id,
          organizationId: org.id,
          organizationName: org.name,
          organizationType: org.type,
          assignedLinks,
          totalRequests: 0,
          isActive: true,
          lastUpdated: new Date().toISOString()
        }
      })

      setLoadBalancers(transformedLoadBalancers)
      setOrganizations(organizationsData)
      setVendorIntakes(vendorIntakesData)
    } catch (err) {
      setError("Failed to load load balancer data")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleConfigureLoadBalancer = (config: LoadBalancerConfig) => {
    setSelectedConfig(config)
    setTempUrls([...config.assignedLinks])
    setShowConfigModal(true)
  }

  const handleSaveConfiguration = async () => {
    const validation = validatePercentages(tempUrls)
    if (!validation.isValid) {
      setError(validation.message)
      return
    }

    try {
      const configurations = tempUrls.map(url => ({
        organizationId: selectedConfig!.organizationId,
        vendorIntakeId: url.linkId,
        percentage: url.percentage
      }))

      await LoadBalancerService.saveConfigurations(configurations)
      
      // Update local state
      setLoadBalancers(prev => 
        prev.map(lb => 
          lb.id === selectedConfig!.id 
            ? { ...lb, assignedLinks: tempUrls, lastUpdated: new Date().toISOString() }
            : lb
        )
      )

      setShowConfigModal(false)
      setSelectedConfig(null)
      setError(null)
    } catch (err) {
      setError("Failed to save configuration")
      console.error(err)
    }
  }

  const handleCreateLoadBalancer = async () => {
    const validation = validatePercentages(newConfig.assignedLinks)
    if (!validation.isValid) {
      setError(validation.message)
        return
      }

    try {
      const configurations = newConfig.assignedLinks.map(url => ({
        organizationId: newConfig.organizationId,
        vendorIntakeId: url.linkId,
        percentage: url.percentage
      }))

      await LoadBalancerService.saveConfigurations(configurations)
      
      // Reload data to get the new configuration
      await loadData()

      setShowCreateModal(false)
      setNewConfig({
        organizationId: "",
        assignedLinks: [],
      })
      setError(null)
    } catch (err) {
      setError("Failed to create load balancer configuration")
      console.error(err)
    }
  }

  const handleDeleteConfiguration = async (configId: string) => {
    if (!window.confirm("Are you sure you want to delete this load balancer configuration?")) return

    try {
      // This would need a delete API endpoint
      setLoadBalancers((prev) => prev.filter((lb) => lb.id !== configId))
    } catch (err) {
      setError("Failed to delete configuration")
      console.error(err)
    }
  }

  const handleUrlChange = (urlId: string, field: keyof LoadBalancerURL, value: any) => {
    setTempUrls(prev => 
      prev.map(url => 
        url.id === urlId ? { ...url, [field]: value } : url
      )
    )
  }

  const handleAutoConfigure = (urls: LoadBalancerURL[]) => {
    setTempUrls(urls)
  }

  const handleAddUrl = () => {
    const newId = `temp-${Date.now()}`
    const newUrls = [...tempUrls, { id: newId, linkId: "", percentage: 0, isActive: true }]
    setTempUrls(autoConfigurePercentages(newUrls))
  }

  const handleRemoveUrl = (urlId: string) => {
    const remainingUrls = tempUrls.filter(url => url.id !== urlId)
    setTempUrls(autoConfigurePercentages(remainingUrls))
  }

  const handleNewConfigUrlChange = (urlId: string, field: keyof LoadBalancerURL, value: any) => {
    setNewConfig(prev => ({
      ...prev,
      assignedLinks: prev.assignedLinks.map(url => 
        url.id === urlId ? { ...url, [field]: value } : url
      )
    }))
  }

  const handleAddNewUrl = () => {
    const newId = `new-${Date.now()}`
    const newUrls = [...newConfig.assignedLinks, { id: newId, linkId: "", percentage: 0, isActive: true }]
    setNewConfig(prev => ({
      ...prev,
      assignedLinks: autoConfigurePercentages(newUrls)
    }))
  }

  const handleRemoveNewUrl = (urlId: string) => {
    const remainingUrls = newConfig.assignedLinks.filter(url => url.id !== urlId)
    setNewConfig(prev => ({
      ...prev,
      assignedLinks: autoConfigurePercentages(remainingUrls)
    }))
  }

  const handleNewConfigAutoConfigure = (urls: LoadBalancerURL[]) => {
    setNewConfig(prev => ({
      ...prev,
      assignedLinks: urls
    }))
  }

  const handleAutoConfigureAll = async () => {
    try {
      setIsAutoConfiguring(true)
      setError(null)
      await LoadBalancerService.autoConfigureAllOrganizations()
      await loadData()
      
    } catch (err) {
      console.error("Error during auto-configuration:", err)
      setError("Failed to auto-configure load balancer")
    } finally {
      setIsAutoConfiguring(false)
    }
  }

  // Filter and pagination logic
  const filteredLoadBalancers = loadBalancers.filter(lb => {
    const matchesSearch = lb.organizationName.toLowerCase().includes(filters.searchTerm.toLowerCase())
    const matchesStatus = filters.statusFilter === "all" || (filters.statusFilter === "active" ? lb.isActive : !lb.isActive)
    const matchesType = filters.typeFilter === "all" || lb.organizationType === filters.typeFilter
    return matchesSearch && matchesStatus && matchesType
  })

  const totalPages = Math.ceil(filteredLoadBalancers.length / pagination.itemsPerPage)
  const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage
  const paginatedLoadBalancers = filteredLoadBalancers.slice(startIndex, startIndex + pagination.itemsPerPage)

  // Update pagination state
  const updatedPagination = { ...pagination, totalPages }

  if (loading) {
    return <LoadBalancerLoading />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        <LoadBalancerHeader 
          onCreateClick={() => setShowCreateModal(true)} 
          onAutoConfigureAll={handleAutoConfigureAll}
          isAutoConfiguring={isAutoConfiguring}
        />
        
        <ErrorDisplay error={error} onDismiss={() => setError(null)} />

        <LoadBalancerFilters
          filters={filters}
          pagination={updatedPagination}
          onFiltersChange={(newFilters: Partial<LoadBalancerFiltersType>) => setFilters(prev => ({ ...prev, ...newFilters }))}
          onPaginationChange={(newPagination: Partial<PaginationState>) => setPagination(prev => ({ ...prev, ...newPagination }))}
        />

        {/* Load Balancer Configurations */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Load Balancer Configurations</h2>
            <p className="text-gray-600">Manage traffic distribution for each organization</p>
      </div>

          {paginatedLoadBalancers.length === 0 ? (
            <LoadBalancerEmptyState onCreateClick={() => setShowCreateModal(true)} />
          ) : (
            <>
              <div className="divide-y divide-gray-200">
                {paginatedLoadBalancers.map((config) => (
                  <LoadBalancerConfigCard
                    key={config.id}
                    config={config}
                    onConfigure={handleConfigureLoadBalancer}
                    onDelete={handleDeleteConfiguration}
                  />
                    ))}
                  </div>

              <LoadBalancerPagination
                pagination={updatedPagination}
                totalItems={filteredLoadBalancers.length}
                onPageChange={(page) => setPagination(prev => ({ ...prev, currentPage: page }))}
              />
            </>
          )}
                        </div>

        <ConfigurationModal
          isOpen={showConfigModal}
          config={selectedConfig}
          tempUrls={tempUrls}
          vendorIntakes={vendorIntakes}
          onClose={() => setShowConfigModal(false)}
          onSave={handleSaveConfiguration}
          onUrlChange={handleUrlChange}
          onAddUrl={handleAddUrl}
          onRemoveUrl={handleRemoveUrl}
          onAutoConfigure={handleAutoConfigure}
        />

        <CreateModal
          isOpen={showCreateModal}
          newConfig={newConfig}
          organizations={organizations}
          vendorIntakes={vendorIntakes}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateLoadBalancer}
          onConfigChange={(config) => setNewConfig(prev => ({ ...prev, ...config }))}
          onUrlChange={handleNewConfigUrlChange}
          onAddUrl={handleAddNewUrl}
          onRemoveUrl={handleRemoveNewUrl}
          onAutoConfigure={handleNewConfigAutoConfigure}
        />
      </div>
    </div>
  )
}